/**
 * @author Mr_FabiozZz[mr.fabiozzz@gmail.com]
 * @author Альберт Евстратов
 */
import React, { createContext, useCallback, useEffect, useRef, useState } from "react";
import HeadPage from "pages/StepPages/StepFour/HeadPage";
import sass from "./roles.module.sass";
import InputTemp from "components-new/InputTemp";
import { Button } from "components/shared";
import { ITableDataModal } from "pages/StepPages/StepFour/One/Tables/models";
import ModalPortal from "components-new/ModalPortal";
import cn from "classnames";
import { BUTTON_COLORS, BUTTON_VARIANTS } from "components/shared/Button/Button";
import { AxiosError } from "axios";
import { useValidate } from "hooks/useValidate";
import Snackbar from "components-new/Snackbar";
import { IEntrySnack } from "components-new/Snackbar/Snackbar";
import useDebounce from "hooks/useDebounce";
import { realData, RecursiveRuleWrapper } from "pages/RolesSetup/RecursiveWrapper";
import { findParent } from "helpers";
import { newState } from "pages/RolesSetup/init";
import { useLocation } from "react-router-dom";
import { usePaginationWithIntersection } from "../../hooks/usePaginationWithIntersection";
import { useAppDispatch } from "../../toolkit/hooks";
import { checkCompletingReferences } from "../../toolkit/references/references.thunks";
import {roleAPI} from "../../libs/apiClient/Role";

interface IRole {
	id?: number;
	name: string;
	system: string | null;
	users: number;
	copy?: number;
}

interface IRoleState {
	user: IRole[];
	system: IRole[];
}

const initial: IRole = {
	name: "",
	users: 0,
	system: null,
};

const CreateRole: React.FC<Omit<ITableDataModal<IRole>, "replace"> & { get: () => void }> = ({
	toggle,
	get,
	close,
	active,
}) => {
	const [role, setRole] = useState<IRole>(initial);
	const dispatch = useAppDispatch();
	useEffect(() => {
		if (active) {
			setRole(active);
		} else {
			setRole(initial);
		}
	}, [active, toggle]);

	const [errors, setErrors] = useState(false);

	const save = useCallback(() => {
		switch (toggle) {
			case "create":
				roleAPI.roleCreate( { name: role.name, ...(role.copy !== undefined && { copy: role.copy }) })
					.then(() => {
						close();
						get();
						dispatch(checkCompletingReferences());
					})
					.catch((error: AxiosError) => {
						if (error.response && error.response.status === 409) {
							setErrors(true);
							setTimeout(() => {
								setErrors(false);
							}, 2500);
						}
					});
				return;
			case "edit":
				roleAPI.roleUpdate(role.id!, { name: role?.name })
					.then(() => {
						close();
						get();
						dispatch(checkCompletingReferences());
					})
					.catch((error: AxiosError) => {
						if (error.response && error.response.status === 409) {
							setErrors(true);
							setTimeout(() => {
								setErrors(false);
							}, 2500);
						}
					});
				return;
			default:
				return;
		}
		//eslint-disable-next-line
	}, [role, toggle]);
	const changeField = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const { name, value } = e.target;
		setRole((prevState) => ({ ...prevState, [name]: value }));
	};

	const { error, validate } = useValidate(role, ["name"], save);

	return toggle !== null ? (
		<ModalPortal
			outSide={false}
			title={toggle === "edit" ? "Редактирование роли" : "Создание роли"}
			handleClose={close}>
			<div className={sass.modal}>
				<InputTemp
					onChange={changeField}
					name={"name"}
					required={true}
					value={role.name}
					error={error && !role.name}
					errorText={errors ? "Роль с таким именем уже существует" : ""}
					placeholder={"Ответственный по актуализации"}
					label={"Название роли"}
				/>
				<div className={sass.modal_btn}>
					<Button onClick={close} variant={BUTTON_VARIANTS.OUTLINED}>
						Отменить
					</Button>
					<Button onClick={validate}>Сохранить</Button>
				</div>
			</div>
		</ModalPortal>
	) : null;
};

const ConfirmRemove: React.FC<{ toggle: boolean; close: () => void; data: IRole | null; get: () => void }> = ({
	toggle,
	get,
	close,
	data,
}) => {
	const dispatch = useAppDispatch();
	const remove = () => {
		if (data) {
			roleAPI.roleDelete(data.id!)
				.then(() => {
					close();
					get();
					dispatch(checkCompletingReferences());
				})
				.catch((error: AxiosError) => {
					let snack: IEntrySnack | undefined = {
						type: "warn",
						position: "top-center",
						title: "Не найдено роли",
						delay: 3000,
						factor: "light",
					};
					if (error.response && error.response.status === 404) {
						Snackbar(snack);
					}
					if (error.response && error.response.status === 401) {
						snack.title = "Вы не авторизованны";
						snack.type = "error";
						Snackbar(snack);
					}
				});
		}
	};

	return toggle ? (
		<ModalPortal outSide={false} handleClose={close} title={"Удалить роль?"}>
			<div className={sass.confirm}>
				<p className={sass.text}>
					Удаляя роль вы <span>сбрасываете роли всех назначенных пользователей.</span> Назначено пользователей:{" "}
					<span>{data?.users || 0}</span>
				</p>
				<div className={sass.modal_btn}>
					<Button variant={BUTTON_VARIANTS.OUTLINED} onClick={close}>
						Отменить
					</Button>
					<Button color={BUTTON_COLORS.ERROR} onClick={remove}>
						Удалить
					</Button>
				</div>
			</div>
		</ModalPortal>
	) : null;
};

export const RolesContext = createContext<{ data: typeof realData; active?: any }>({} as { data: typeof realData });
const RolesSetup = () => {
	const modifyData: <T>(arr: T) => Array<string> = (arr) => {
		const upload: Array<string> = [];
		let arrKey: keyof typeof arr;
		for (arrKey in arr) {
			if (arr[arrKey]) {
				upload.push(arrKey);
			}
		}
		return upload.filter((_) => /\./.test(_ as string));
	};
	const location = useLocation();
	const filteredArr = (arr: string[], formattingTarget: typeof realData) => {
		let parents: string[] = [];
		arr.forEach((_) => {
			const parent = findParent(newState, _, "fields");
			let flag = false;
			if (parent && parent.fields) {
				for (let i = 0; i < parent.fields.length; i++) {
					const item = parent.fields[i];
					if (item && !formattingTarget[item.id]) {
						flag = false;
						break;
					} else {
						flag = true;
					}
				}
				flag && parents.push(parent.id);
			}
		});
		parents = Array.from(new Set(parents));
		parents.forEach((_) => {
			formattingTarget[_] = true;
		});
		if (parents.length) {
			filteredArr(parents, formattingTarget);
		} else {
			return;
		}
	};

	const [loading, setLoading] = useState(true);

	const [dataRoles, setDataRoles] = useState(realData);

	const [roles, setRoles] = useState<IRoleState>({
		user: [],
		system: [],
	});

	const [search, setSearch] = useState("");

	const query = useDebounce(search, 500);

	const [rolesGetParams, setRolesGetParams] = useState<{
		page: number;
		num: number;
		query: string | null;
	}>({
		page: 1,
		num: 30,
		query: null,
	});

	useEffect(() => {
		if (query) {
			setRolesGetParams((prevState) => ({ ...prevState, query }));
		} else {
			setRolesGetParams((prevState) => ({ ...prevState, query: null }));
		}
	}, [query]);

	const [active, setActive] = useState<IRole>({ id: 0, name: "", users: 0, system: null });

	const [confirm, setConfirm] = useState<{ data: IRole | null; active: boolean }>({ data: null, active: false });

	const [modal, setModal] = useState<{ mode: "create" | "edit" | null; data: IRole | null | undefined }>({
		data: null,
		mode: null,
	});

	const sendingData = useDebounce(dataRoles, 1000);

	const send = useCallback(
		(arr?: string[]) => {
			if (active.id! > 0 && active.system === null) {
				const grant = modifyData(dataRoles);
				if (arr) {
					roleAPI.permissionsUpdate(active.id!, { grant: arr, replace: true }).then((r) => r);
				} else {
					roleAPI.permissionsUpdate(active.id!, { grant, replace: true }).then((r) => r);
				}
			}
		},
		[dataRoles, active],
	);

	const recursiveCheck = (key: string, target: typeof realData) => {
		const parent = findParent(newState, key, "fields");
		if (parent && parent.fields) {
			let flag = false;
			for (let i = 0; i < parent.fields.length; i++) {
				if (!target[parent.fields[i].id]) {
					flag = false;
					break;
				} else {
					flag = true;
				}
			}
			target[parent.id] = flag;
			const grandPa = findParent(newState, parent.id, "fields");
			if (grandPa) recursiveCheck(parent.id, target);
		}
		return null;
	};

	const changeData = (keyId: string | Array<string>, value: boolean | null = null) => {
		if (typeof keyId === "string") {
			setDataRoles((item) => {
				const copyState = { ...item };
				copyState[keyId] = value !== null ? value : !copyState[keyId];
				recursiveCheck(keyId, copyState);
				return copyState;
			});
		} else {
			let result = { ...dataRoles };
			let dataKey: keyof typeof realData;
			for (dataKey in dataRoles) {
				if (keyId.includes(dataKey)) {
					result[dataKey] = value !== null ? value : !result[dataKey];
				}
			}
			// filteredArr(keyId,result)
			setDataRoles((prevState) => ({ ...prevState, ...result }));
		}
	};

	useEffect(() => {
		if (!loading) {
			send(modifyData(sendingData));
		} else {
			setLoading(false);
		}
		//eslint-disable-next-line
	}, [sendingData]);

	useEffect(() => {
		setLoading(true);
		if (active && active.id && active.id > 0) {
			roleAPI.permissionsDetail(active.id!).then((r) => {
				/* сначала отмечаю то что приходит с сервера */
				let result = { ...realData };
				let dataKey: keyof typeof realData;
				for (dataKey in dataRoles) {
					result[dataKey] = r.data.includes(dataKey);
				}
				/* дальше фильтрую result и вытаскиваю ключи оз отфильтрованного объекта */
				let trustyResult: string[] = [];
				let resultKey: keyof typeof result;
				for (resultKey in result) {
					if (result[resultKey]) trustyResult.push(resultKey);
				}
				/* дальше ищу родительские элементы в объекте структуры
				 * и если все дочерние отмечены, ставлю положительное значение родителю
				 * идет рекурсивно до корня */
				filteredArr(trustyResult, result);
				setDataRoles((prevState) => ({ ...prevState, ...result }));
			});
		}
		//eslint-disable-next-line
	}, [active]);
	useEffect(() => {
		return () => {
			send();
		};
		//eslint-disable-next-line
	}, []);

	useEffect(() => {
		if (location.state && location.state.outline) {
			let findRole = roles.user.find((_) => _.id === location.state.outline);
			if (!findRole) findRole = roles.system.find((_) => _.id === location.state.outline);
			findRole && setActive(findRole);
		}
	}, [location.state, roles]);
	const thenHandler = useCallback(
		(data: IRoleState) => {
			const upPage = (prevState: typeof rolesGetParams) => ({ ...prevState, page: prevState.page + 1 });
			if (rolesGetParams.page === 1) {
				if (data.system.length || data.user.length) {
					if (data.user.length + data.system.length < rolesGetParams.num) {
						setIsFinally(true);
					} else {
						setRolesGetParams(upPage);
					}
				} else {
					setIsFinally(true);
				}
				setRoles(data);
			} else {
				if (data.user.length || data.system.length) {
					if (data.user.length + data.system.length < rolesGetParams.num) {
						setIsFinally(true);
					} else {
						setRolesGetParams(upPage);
					}
					setRoles((prevState) => ({
						user: [...prevState.user, ...data.user],
						system: [...prevState.system, ...data.system],
					}));
				} else {
					setIsFinally(true);
				}
			}
			//eslint-disable-next-line
		}, [rolesGetParams],
	);

	const reloadRoles = () => {
		setRolesGetParams((prevState) => ({ ...prevState, page: 1 }));
		setIsFinally(false);
		setFetch(true);
	};

	const lastTarget = useRef<HTMLDivElement | null>(null);
	const wrapper = useRef<HTMLDivElement | null>(null);
	const { setIsFinally, setFetch } = usePaginationWithIntersection({
		link: "/role/list",
		params: rolesGetParams,
		then: (e: any) => thenHandler(e),
		dependencies: [wrapper.current, lastTarget.current],
		lastTarget: lastTarget,
		ref: wrapper,
		catchHandler: () => {
			setIsFinally(true);
		},
	});

	return (
		<div className={"step-wrapper"}>
			{/*<Test/>*/}
			<ConfirmRemove
				get={reloadRoles}
				toggle={confirm.active}
				close={() => setConfirm({ data: null, active: false })}
				data={confirm.data}
			/>
			<CreateRole
				get={reloadRoles}
				toggle={modal.mode}
				close={() => setModal({ data: null, mode: null })}
				active={modal.data}
			/>
			<HeadPage title={"Настройка ролей пользователей"} />
			<div className={sass.wrapper}>
				<p className={sass["wrapper-header"]}>Выберите роль для настройки</p>
				<p className={sass[`wrapper-${active.name ? "header" : "empty"}`]}>
					Задайте права взаимодействия с системой для выбранной роли
				</p>
				<div className={sass.roles}>
					<div className={sass["list-wrapper"]}>
						<div className={sass.search}>
							<InputTemp
								value={search}
								onChange={(e) => {
									setSearch(e.target.value);
								}}
								searchIcon={true}
								placeholder={"Поиск"}
							/>
						</div>
						<div data-comment-false={true} className={sass.list} ref={wrapper}>
							{!!roles.system.length && (
								<>
									<div className={sass.itemTitle}>Основные</div>
									{roles.system.map((_, index) => {
										return (
											<div
												key={index}
												onClick={() => {
													active.id !== _.id && send();
													setActive(_);
												}}
												className={cn(sass.item, { [sass.active]: _.name === active.name })}>
												{_.name}
												<div className={sass.iconsBox}>
													<div
														onClick={() =>
															setModal({ mode: "create", data: { copy: _.id, name: "", system: null, users: 0 } })
														}
														className={sass.icon}>
														<div className={sass["copy-icon"]} />
													</div>
												</div>
											</div>
										);
									})}
								</>
							)}
							{!!roles.user.length && (
								<>
									<div className={sass.itemTitle}>Созданные</div>
									{roles.user.map((_, index) => {
										return (
											<div
												key={index}
												onClick={() => {
													active.id !== _.id && send();
													setActive(_);
												}}
												className={cn(sass.item, { [sass.active]: _.name === active.name })}>
												{_.name}
												<div className={sass.iconsBox}>
													<div
														onClick={() =>
															setModal({ mode: "create", data: { copy: _.id, name: "", system: null, users: 0 } })
														}
														className={sass.icon}>
														<div className={sass["copy-icon"]} />
													</div>
													<div onClick={() => setModal({ mode: "edit", data: _ })} className={sass.icon}>
														<div className={sass["edit-icon"]} />
													</div>
													<div className={sass.icon} onClick={() => setConfirm({ active: true, data: _ })}>
														<div className={sass["trash-icon"]} />
													</div>
												</div>
											</div>
										);
									})}
								</>
							)}
							<div ref={lastTarget} />
							{/*{roles.length ? (*/}
							{/*	roles.map((_, index) => {*/}
							{/*		return (*/}
							{/*			<div*/}
							{/*				key={index}*/}
							{/*				onClick={() => {*/}
							{/*					active.id !== _.id && send();*/}
							{/*					setActive(_);*/}
							{/*				}}*/}
							{/*				className={cn(sass.item, { [sass.active]: _.name === active.name })}>*/}
							{/*				{_.name}*/}
							{/*				<div className={sass.iconsBox}>*/}
							{/*					<div onClick={() => setModal({ mode: "edit", data: _ })} className={sass.icon}>*/}
							{/*						<div className={sass["edit-icon"]} />*/}
							{/*					</div>*/}
							{/*					<div className={sass.icon} onClick={() => setConfirm({ active: true, data: _ })}>*/}
							{/*						<div className={sass["trash-icon"]} />*/}
							{/*					</div>*/}
							{/*				</div>*/}
							{/*			</div>*/}
							{/*		);*/}
							{/*	})*/}
							{/*) : (*/}
							{/*	<p>Ролей пока нет</p>*/}
							{/*)}*/}
						</div>
					</div>
					<Button
						onClick={() => {
							setModal({ data: null, mode: "create" });
						}}>
						Создать роль
					</Button>
				</div>

				<div data-comment-false={true} className={active.name ? sass.rolesList : sass.empty}>
					{active.name ? (
						<RolesContext.Provider value={{ data: dataRoles, active }}>
							<RecursiveRuleWrapper change={changeData} />
						</RolesContext.Provider>
					) : (
						<div className={sass.emptyText}>Выберите роль или создайте новую</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default RolesSetup;
